<script setup>
import HeaderTitle from "@/components/layout/HeaderTitle.vue";
import ScheduleDialog from "@/components/shared/Schedules/ScheduleDialog.vue";
import SharedElementPagination from "@/components/shared/SharedElementPagination.vue";
import SharedDeleteConfirmDialog from "@/components/shared/SharedDeleteConfirmDialog.vue";
import ScheduleService from "@/api/services/schedule.service";
import Notification from "@/utils/plugins/NotificationService";
import { useAutomaticSearch } from "@/app/composables/useAutomaticSearch";
import { onMounted, ref } from "vue";
import { useUiStore } from "@/app/stores/uiStore";

const uiStore = useUiStore();
const schedules = ref([]);
const selectedSchedule = ref(null);
const selectedScheduleToDelete = ref(null);
const showScheduleDialog = ref(false);
const deleteDialogShown = ref(false);
const scheduleName = ref("");
const page = ref(1);
const itemsPerPage = ref(10);
const totalResults = ref(0);

onMounted(() => {
  getSchedules();
});

const { beginRequest, isCurrentRequest } = useAutomaticSearch(scheduleName, () => {
  page.value = 1;
  getSchedules();
});

const handlePageChange = (newPage) => {
  page.value = newPage;
  getSchedules();
};

const getSchedules = () => {
  const params = {
    page: page.value,
    itemsPerPage: itemsPerPage.value,
  };

  if (scheduleName.value) {
    params.name = scheduleName.value;
  }

  uiStore.startLoading();
  const requestId = beginRequest();
  ScheduleService.getSchedules(params)
    .then((res) => {
      if (!isCurrentRequest(requestId)) return;
      schedules.value = res.data["hydra:member"];
      totalResults.value = res.data["hydra:totalItems"];
    })
    .catch(() => {
      if (isCurrentRequest(requestId)) Notification.addNotification("Error al obtener los horarios", "error", 6000);
    })
    .finally(() => {
      uiStore.stopLoading();
    });
};

const openScheduleDialog = (schedule = null) => {
  selectedSchedule.value = schedule ? { ...schedule } : null;
  showScheduleDialog.value = true;
};

const closeScheduleDialog = () => {
  selectedSchedule.value = null;
  showScheduleDialog.value = false;
};

const openDeleteDialog = (schedule) => {
  selectedScheduleToDelete.value = schedule;
  deleteDialogShown.value = true;
};

const cleanSchedulePayload = (schedule) => {
  const payload = { ...schedule };
  delete payload["@id"];
  delete payload["@type"];
  delete payload.employees;
  delete payload.id;
  return payload;
};

const getResourceId = (resource) => {
  if (!resource) {
    return null;
  }

  if (resource.id) {
    return resource.id;
  }

  if (resource["@id"]) {
    return String(resource["@id"]).split("/").pop();
  }

  return null;
};

const saveSchedule = async ({ schedule }) => {
  uiStore.startLoading();
  try {
    const scheduleId = getResourceId(schedule) || getResourceId(selectedSchedule.value);
    const scheduleToSave = {
      ...schedule,
      id: scheduleId,
      "@id": schedule["@id"] || selectedSchedule.value?.["@id"],
    };
    if (scheduleId) {
      await updateSchedule(scheduleToSave);
    } else {
      await createSchedule(scheduleToSave);
    }

    Notification.addNotification({
      title: "Horarios",
      message: scheduleId ? "Horario actualizado correctamente" : "Horario creado correctamente",
      type: "success",
    });
    closeScheduleDialog();
    getSchedules();
  } catch (error) {
    Notification.addNotification("Error al guardar el horario", "error", 6000);
  } finally {
    uiStore.stopLoading();
  }
};

const createSchedule = async (schedule) => {
  const res = await ScheduleService.createSchedule(cleanSchedulePayload(schedule));
  return res.data;
};

const updateSchedule = async (schedule) => {
  const scheduleId = getResourceId(schedule);
  const res = await ScheduleService.updateSchedule(cleanSchedulePayload(schedule), scheduleId);
  return res.data;
};

const deleteSchedule = (schedule) => {
  if (!schedule?.id) {
    deleteDialogShown.value = false;
    return;
  }

  uiStore.startLoading();
  ScheduleService.deleteSchedule(schedule.id)
    .then(() => {
      Notification.addNotification("Horario eliminado correctamente", "success", 6000);
      deleteDialogShown.value = false;
      selectedScheduleToDelete.value = null;
      if (schedules.value.length === 1 && page.value > 1) {
        page.value -= 1;
      }
      getSchedules();
    })
    .catch(() => {
      Notification.addNotification("Error al eliminar el horario", "error", 6000);
    })
    .finally(() => {
      uiStore.stopLoading();
    });
};

const scheduleDaysText = (schedule) => {
  const days = [];
  if (schedule.mon) days.push("Lun");
  if (schedule.tue) days.push("Mar");
  if (schedule.wed) days.push("Mie");
  if (schedule.thu) days.push("Jue");
  if (schedule.fri) days.push("Vie");
  return days.length ? days.join(", ") : "--";
};

const scheduleTimeText = (start, end) => {
  return start && end ? `${start}-${end}` : "--";
};

</script>

<template>
  <div class="schedules">
    <HeaderTitle title="Ajustes" />
    <div class="d-flex flex-row justify-content-start">
      <font-awesome-icon class="schedule-title-icon" icon="calendar-alt" />
      <div class="title-text pl-4">
        Horarios
      </div>
    </div>

    <el-card class="card-search w-100 wback" body-style="padding:0">
      <div class="row mx-5 schedule-search align-items-end">
        <div class="col-md-4 col-12">
          <div class="form-item">
            <label for="scheduleSearchName" class="w-100 mb-2">Nombre</label>
            <el-input id="scheduleSearchName" v-model="scheduleName" class="w-100" placeholder="Nombre del horario"
              clearable />
          </div>
        </div>
        <div class="col-md-4 offset-md-4 col-12 d-flex justify-content-end schedule-search-actions">
          <button class="btn btn_darkpink btn-search schedule-new-button" type="button" @click="openScheduleDialog()">
            NUEVO HORARIO
          </button>
        </div>
      </div>
    </el-card>

    <div class="table table-not-mobile schedule-table">
      <div v-if="schedules.length > 0">
        <table>
          <thead>
            <tr>
              <th>Nombre</th>
              <th>Días</th>
              <th>Mañana</th>
              <th>Tarde</th>
              <th class="schedule-actions-col">Acciones</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="schedule in schedules" :key="schedule.id" class="pointer" @click="openScheduleDialog(schedule)">
              <td>
                {{ schedule.name }}
              </td>
              <td>
                {{ scheduleDaysText(schedule) }}
              </td>
              <td>
                {{ scheduleTimeText(schedule.timeEnterMorning, schedule.timeExitMorning) }}
              </td>
              <td>
                {{ scheduleTimeText(schedule.timeEnterAfternoon, schedule.timeExitAfternoon) }}
              </td>
              <td class="schedule-actions-col">
                <div class="buttons d-inline-flex">
                  <font-awesome-layers class="fa-2x pointer" style="margin-left: 5px" title="Editar"
                    @click.stop="openScheduleDialog(schedule)">
                    <font-awesome-icon icon="circle" style="color: #f7d20e" />
                    <font-awesome-icon icon="plus" class="fa-inverse" transform="shrink-6" />
                  </font-awesome-layers>
                  <font-awesome-layers class="fa-2x pointer" style="margin-left: 5px" title="Eliminar"
                    @click.stop="openDeleteDialog(schedule)">
                    <font-awesome-icon icon="circle" style="color:red" />
                    <font-awesome-icon icon="times" class="fa-inverse" transform="shrink-6" />
                  </font-awesome-layers>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
      <div v-else>
        <h5>No se han encontrado horarios</h5>
      </div>
    </div>

    <div class="table-mobile">
      <div v-if="schedules.length > 0">
        <div v-for="schedule in schedules" :key="schedule.id">
          <el-row class="mt-3">
            <el-card class="card-schedule-item">
              <div class="schedule-card-delete" @click.stop>
                <font-awesome-layers class="fa-2x pointer" title="Eliminar" @click.stop="openDeleteDialog(schedule)">
                  <font-awesome-icon icon="circle" style="color:red" />
                  <font-awesome-icon icon="times" class="fa-inverse" transform="shrink-6" />
                </font-awesome-layers>
              </div>
              <div class="row d-flex pointer" @click="openScheduleDialog(schedule)">
                <div class="el-col-xs-24">
                  <span class="schedule-item-span">Nombre: </span>{{ schedule.name }}
                </div>
                <div class="el-col-xs-24">
                  <span class="schedule-item-span">Días: </span>{{ scheduleDaysText(schedule) }}
                </div>
                <div class="el-col-xs-24">
                  <span class="schedule-item-span">Mañana: </span>{{
                    scheduleTimeText(schedule.timeEnterMorning, schedule.timeExitMorning)
                  }}
                </div>
                <div class="el-col-xs-24">
                  <span class="schedule-item-span">Tarde: </span>{{
                    scheduleTimeText(schedule.timeEnterAfternoon, schedule.timeExitAfternoon)
                  }}
                </div>
              </div>
            </el-card>
          </el-row>
        </div>
      </div>
      <div v-else>
        <h5>No se han encontrado horarios</h5>
      </div>
    </div>

    <SharedElementPagination class="pagination" v-model:current-page="page" v-model:page-size="itemsPerPage"
      :total="totalResults" style="padding: 0px 0px 20px 0px;" @change="handlePageChange" show-always />

    <el-dialog v-model="showScheduleDialog" class="el-dialog schedule-dialog" width="850" :show-close="false"
      :center="true" :lock-scroll="true" :style="{ borderRadius: `var(--el-border-radius-round)` }"
      @closed="closeScheduleDialog">
      <ScheduleDialog v-if="showScheduleDialog" :selected-schedule="selectedSchedule"
        @close-dialog="closeScheduleDialog" @save="saveSchedule" />
    </el-dialog>

    <SharedDeleteConfirmDialog v-model="deleteDialogShown" title="Eliminar horario"
      message="¿Desea eliminar permanentemente este horario?" @confirm="deleteSchedule(selectedScheduleToDelete)"
      @cancel="selectedScheduleToDelete = null" />
  </div>
</template>

<style lang="scss" scoped>
.schedule-title-icon {
  color: $bg-blue;
  font-size: 30px;
  margin-top: 7px;
}

.schedule-search .form-item {
  margin-bottom: 0;
}

.schedule-new-button {
  width: 100%;
  max-width: 300px;
  min-height: 44px;
  margin-bottom: 0 !important;
  color: white;
}

.schedule-table {
  padding-top: 20px;
}

.schedule-actions-col {
  width: 110px;
  min-width: 110px;
  text-align: center;
}

.pagination {
  padding: 0px 0px 20px 0px;
}

@media (min-width: 768px) {
  .table-mobile {
    display: none;
  }
}

@media (max-width: 767px) {
  .table-not-mobile {
    display: none;
  }

  .schedule-search {
    margin-left: 0 !important;
    margin-right: 0 !important;
    row-gap: 16px;
  }

  .schedule-search-actions {
    flex-direction: column;
    align-items: stretch !important;
  }

  .schedule-search-actions .btn {
    width: 100%;
    max-width: none !important;
  }

  .card-schedule-item {
    position: relative;
    padding: 10px 0px;
    border-radius: 16px;
    font-size: 15px;
  }

  .schedule-card-delete {
    position: absolute;
    top: 8px;
    right: 10px;
    z-index: 1;
  }

  .schedule-item-span {
    color: $bg-blue;
    font-weight: 800;
    font-size: 16px;
  }

}
</style>

<style lang="scss">
.schedule-delete-dialog {
  .el-dialog__header {
    padding-bottom: 8px;
  }

  .el-dialog__body {
    padding: 0 20px 6px;
  }

  .el-dialog__footer {
    padding-top: 8px;
  }
}
</style>
